import { useState } from 'react';
import { Check, Pencil, Plus, Star, Trash2, Mail, Phone, X } from 'lucide-react';
import { useDialog } from '../../context/DialogContext';
import {
  useGuestPartyMembers,
  useCreatePartyMember,
  useUpdatePartyMember,
  useDeletePartyMember,
} from '../../hooks/useInvites';

/**
 * Producer-managed contacts for a guest "party" (a couple or family sharing
 * one guest record). Each member carries a name plus optional email/phone; a
 * campaign send reaches every member email, and an RSVP confirmation notifies
 * them all. Embedded in the guest edit panel — only for an existing guest,
 * since members attach to a saved guest id.
 */
export function GuestPartyMembersSection({ guestId }: { guestId: string }) {
  const { confirm } = useDialog();
  const { data: members = [] } = useGuestPartyMembers(guestId);
  const createMember = useCreatePartyMember();
  const updateMember = useUpdatePartyMember();
  const deleteMember = useDeletePartyMember();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Inline edit of an existing member's contact details.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');

  const startEdit = (member: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  }) => {
    setEditingId(member.id);
    setEditName(member.name);
    setEditEmail(member.email ?? '');
    setEditPhone(member.phone ?? '');
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (memberId: string) => {
    if (!editName.trim()) return;
    await updateMember.mutateAsync({
      guestId,
      memberId,
      updates: {
        name: editName.trim(),
        email: editEmail.trim() || null,
        phone: editPhone.trim() || null,
      },
    });
    setEditingId(null);
  };

  const handleAdd = async () => {
    if (!name.trim()) return;
    await createMember.mutateAsync({
      guestId,
      input: {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        isPrimary: members.length === 0,
        sortOrder: members.length,
      },
    });
    setName('');
    setEmail('');
    setPhone('');
  };

  const handleDelete = async (memberId: string, memberName: string) => {
    const ok = await confirm({
      message: `Remove ${memberName} from this party?`,
      variant: 'danger',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    await deleteMember.mutateAsync({ guestId, memberId });
  };

  const setPrimary = async (memberId: string) => {
    await updateMember.mutateAsync({ guestId, memberId, updates: { isPrimary: true } });
  };

  return (
    <div className="space-y-2" data-testid="guest-party-members-section">
      <div>
        <label className="block text-sm font-medium text-gray-700">Party Contacts</label>
        <p className="text-xs text-gray-500">
          Add each person in a couple/family with their own email. Invites and RSVP confirmations go
          to everyone here, and any of them can respond for the whole party.
        </p>
      </div>

      {members.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {members.map((member) =>
            editingId === member.id ? (
              <li key={member.id} className="px-3 py-2 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    aria-label={`Edit name for ${member.name}`}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="Email (optional)"
                    aria-label={`Edit email for ${member.name}`}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="Phone (optional)"
                    aria-label={`Edit phone for ${member.name}`}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="flex justify-end gap-1">
                  <button
                    type="button"
                    aria-label={`Save changes to ${member.name}`}
                    onClick={() => void saveEdit(member.id)}
                    disabled={!editName.trim()}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Save
                  </button>
                  <button
                    type="button"
                    aria-label={`Cancel editing ${member.name}`}
                    onClick={cancelEdit}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                </div>
              </li>
            ) : (
              <li key={member.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {member.name}
                    </span>
                    {member.isPrimary && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-gray-500">
                    {member.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {member.email}
                      </span>
                    )}
                    {member.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {member.phone}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!member.isPrimary && (
                    <button
                      type="button"
                      aria-label={`Make ${member.name} the primary contact`}
                      title="Make primary"
                      onClick={() => void setPrimary(member.id)}
                      className="p-1.5 text-gray-400 hover:text-amber-500 rounded"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label={`Edit ${member.name}`}
                    onClick={() => startEdit(member)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${member.name}`}
                    onClick={() => void handleDelete(member.id, member.name)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ),
          )}
        </ul>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          aria-label="Party member name"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional)"
          aria-label="Party member email"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone (optional)"
          aria-label="Party member phone"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        />
      </div>
      <button
        type="button"
        onClick={() => void handleAdd()}
        disabled={!name.trim() || createMember.isPending}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        Add contact
      </button>
    </div>
  );
}
